import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Search, Trash2, RefreshCw, Inbox } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AnalyticsSection from "@/components/admin/AnalyticsSection";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const LOGO =
  "https://customer-assets.emergentagent.com/job_ed2227e8-f42f-486d-80b3-32e833e48098/artifacts/o750zne6_72B3D45F-1688-469F-935E-BB6BB3789CC9.png";

const STATUS_OPTIONS = ["new", "contacted", "quoted", "won", "lost"];

const SERVICE_LABELS = {
  snow_removal: "Snow Removal",
  salting_deicing: "Salting & De-Icing",
  driveway_sealing: "Driveway Sealing",
  other: "Other",
};

const STATUS_STYLES = {
  new: "bg-[#9BD0E8]/15 text-[#9BD0E8] border-[#9BD0E8]/30",
  contacted: "bg-blue-400/15 text-blue-300 border-blue-400/30",
  quoted: "bg-[#C9A24B]/15 text-[#C9A24B] border-[#C9A24B]/40",
  won: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  lost: "bg-red-400/10 text-red-300 border-red-400/30",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, won: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/admin/stats"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        l.name.toLowerCase().includes(q) ||
        l.email.toLowerCase().includes(q) ||
        l.phone.toLowerCase().includes(q) ||
        l.property_address.toLowerCase().includes(q)
      );
    });
  }, [leads, statusFilter, search]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, { status });
      setLeads((curr) => curr.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((curr) => curr.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Lead deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#050B14] text-white">
      <header className="border-b border-white/10 bg-[#050B14]/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img src={LOGO} alt="Capital Shield" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-display text-base font-bold tracking-wide">CAPITAL SHIELD</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#9BD0E8]">
                Lead Console
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-xs text-white/60">{user?.email}</div>
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 hover:border-[#C9A24B] hover:text-[#C9A24B] text-xs tracking-wide transition-colors"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A24B] hover:bg-[#E0B962] text-[#050B14] text-xs font-semibold tracking-wide transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <h1 className="font-display text-3xl md:text-4xl font-bold mb-2">Leads</h1>
        <p className="text-white/55 text-sm mb-8">
          All quote requests submitted via the site.
        </p>
        <div className="grid grid-cols-3 gap-4 mb-10">
          <StatCard label="Total leads" value={stats.total} testid="stat-total" />
          <StatCard label="New" value={stats.new} accent testid="stat-new" />
          <StatCard label="Won" value={stats.won} testid="stat-won" />
        </div>

        <div className="bg-[#0B1B3D] border border-white/10 p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone, address…"
              className="bg-[#050B14] border-white/15 text-white placeholder:text-white/30 rounded-none pl-9 h-11 focus-visible:ring-[#C9A24B] focus-visible:ring-1"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              data-testid="admin-status-filter"
              className="bg-[#050B14] border-white/15 text-white rounded-none h-11 w-full md:w-56"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0B1B3D] border-white/15 text-white rounded-none">
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-[#0B1B3D] border border-white/10 overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-white/50 text-sm">Loading leads…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-white/50">
              <Inbox size={32} className="mx-auto mb-4 text-white/30" />
              <div className="text-sm">No leads match your filters.</div>
            </div>
          ) : (
            <Table data-testid="admin-leads-table">
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-white/50">Name</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-white/50">Contact</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-white/50">Service</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-white/50">Property</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-white/50">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-white/50">Received</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-[0.2em] text-white/50">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((l) => (
                  <TableRow
                    key={l.id}
                    data-testid={`lead-row-${l.id}`}
                    className="border-white/10 hover:bg-[#0F2554]/60 cursor-pointer"
                    onClick={() => setSelected(l)}
                  >
                    <TableCell className="font-semibold text-white">{l.name}</TableCell>
                    <TableCell className="text-white/70 text-xs">
                      <div>{l.email}</div>
                      <div className="text-white/50">{l.phone}</div>
                    </TableCell>
                    <TableCell className="text-white/80 text-sm">{SERVICE_LABELS[l.service] || l.service}</TableCell>
                    <TableCell className="text-white/70 text-xs max-w-[240px]">
                      <div className="truncate">{l.property_address}</div>
                      <div className="text-white/50 truncate">{l.property_size}</div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                        <SelectTrigger
                          data-testid={`lead-status-${l.id}`}
                          className={`h-8 w-32 rounded-none border text-xs capitalize ${STATUS_STYLES[l.status] || ""}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0B1B3D] border-white/15 text-white rounded-none">
                          {STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-white/55 text-xs whitespace-nowrap">
                      {new Date(l.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => remove(l.id)}
                        data-testid={`lead-delete-${l.id}`}
                        className="text-white/50 hover:text-red-400 p-2"
                        aria-label="Delete lead"
                      >
                        <Trash2 size={14} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <AnalyticsSection />
      </div>

      {selected && (
        <div
          data-testid="lead-detail-overlay"
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-end md:items-center md:justify-end"
          onClick={() => setSelected(null)}
        >
          <aside
            className="w-full md:w-[480px] md:h-full bg-[#0B1B3D] border-l border-white/10 p-8 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
              <div className="text-xs uppercase tracking-[0.25em] text-[#C9A24B] font-semibold">
                Lead Detail
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="lead-detail-close"
                className="text-white/60 hover:text-white text-sm"
              >
                Close ×
              </button>
            </div>
            <h2 className="font-display text-2xl font-semibold mb-1">{selected.name}</h2>
            <div className="text-sm text-white/60 mb-8">
              {new Date(selected.created_at).toLocaleString()}
            </div>

            <DetailRow label="Email" value={selected.email} />
            <DetailRow label="Phone" value={selected.phone} />
            <DetailRow label="Service" value={SERVICE_LABELS[selected.service] || selected.service} />
            <DetailRow label="Property address" value={selected.property_address} />
            <DetailRow label="Property size" value={selected.property_size} />
            <DetailRow label="Status" value={selected.status} />

            {selected.message && (
              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Message</div>
                <div className="text-sm text-white/85 bg-[#050B14] border border-white/10 p-4 leading-relaxed whitespace-pre-wrap">
                  {selected.message}
                </div>
              </div>
            )}
          </aside>
        </div>
      )}
    </main>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className={`border p-6 ${accent ? "bg-[#C9A24B]/10 border-[#C9A24B]/30" : "bg-[#0B1B3D] border-white/10"}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mb-3 font-semibold">{label}</div>
      <div className={`font-display text-4xl font-bold ${accent ? "text-[#C9A24B]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="py-3 border-b border-white/10">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">{label}</div>
      <div className="text-sm text-white capitalize">{value}</div>
    </div>
  );
}
